import { Routes, ActivatedRoute, ParamMap } from '@angular/router';
import { SolveUnmatchedGoodsService } from './solve-unmatched-goods.service';
import { Component, OnInit, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { NgxFileDropEntry, FileSystemFileEntry } from 'ngx-file-drop';
import { RouterService } from 'src/app/services/router/router.service';
import { MaintenanceMenuService } from 'src/app/shared/maintenance-menu/maintenance-menu.service';
import { MessageFieldComponent } from 'src/app/shared/message-field/message-field.component';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'src/environments/environment';
import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { AppAuthService } from 'src/app/services/auth/app-auth.service';

@Component({
  selector: 'app-solve-unmatched-goods',
  templateUrl: './solve-unmatched-goods.component.html',
  styleUrls: ['./solve-unmatched-goods.component.scss']
})
export class SolveUnmatchedGoodsComponent implements OnInit, AfterViewInit {
  @ViewChild(MessageFieldComponent, { static: false }) messageFieldComponent: MessageFieldComponent;
  @ViewChild('downloadLinkRef') public downloadLinkRef: ElementRef;
  private FileData: NgxFileDropEntry;
  private allowedExtension = '.xlsx';
  /* チェーンリスト */
  public chainList = [];
  /* フィルタリングされたチェーンリスト */
  public filteredChainList: Observable<{}>;
  /* チェーン選択オートコンプリートフォーム */
  public chainListControl = new FormControl();
  /** アッマッチビューのディレクトリ */
  public tableauUmmatchedDir: string = '';

  /**
   * コンストラクタ
   */
  constructor(
    public maintenanceMenuService: MaintenanceMenuService,
    public solveUnmatchedGoodsService: SolveUnmatchedGoodsService,
    private routerService: RouterService,
    private toastr: ToastrService,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.routerService.setPreviousPage();

    this.solveUnmatchedGoodsService.initialize(environment.organization).subscribe(res => {
      // チェーンリスト取得
      this.chainList = res.chainList;

      // チェーンリストサジェスチョン設定
      this.filteredChainList = this.chainListControl.valueChanges
        .pipe(
          startWith(''),
          map(value => this.autoCompleteFilter(value))
        );

      // チェーンリスト変更時設定
      this.chainListControl.valueChanges.subscribe(x => {
        if (!x) {
          return;
        }
        const chain = this.chainList.find(c => c.chainCd === x);
        if (!chain) {
          return;
        }
        this.tableauUmmatchedDir = chain.view;
      });
      this.route.paramMap.subscribe((p: ParamMap) => {
        // URLからチェーンCDを取得
        const chain = this.chainList.find(x => x.chainCd === p.get('chainCd'));
        if (chain !== undefined && chain !== null) {
          this.chainListControl.setValue(chain.chainCd);
        } else {
          return;
        }
      });
    });
  }

  ngAfterViewInit(): void {
  }

  /**
   * アップロードファイル設定
   * @param file ファイル
   * @returns
   */
  public setFile(file: NgxFileDropEntry) {
    // Excelファイル未選択
    if (file !== null && !file.relativePath.endsWith(this.allowedExtension)) {
      this.toastr.error('Excelファイル以外が選択されています。ファイルを再選択してください", "Excelファイル非選択');
      return;
    }
    this.FileData = file;
  }

  /**
   * ファイルのアップロード処理
   */
  public uploadFile() {
    const formData = new FormData();
    // ファイル未選択
    if (!this.FileData || !this.FileData.fileEntry.isFile) {
      this.toastr.error('アンマッチ商品ファイルが選択されていません。', 'アンマッチ商品エラー');
      return;
    }

    // チェーン選択
    const chain = this.chainList.find(x => x.chainCd === this.chainListControl.value);
    if (!chain) {
      this.toastr.error('チェーンを選択してください。', 'アンマッチ商品エラー');
      return;
    }
    formData.append('chainCd', chain.chainCd);
    formData.append('organization', environment.organization);

    const fileEntry = this.FileData.fileEntry as FileSystemFileEntry;
    fileEntry.file((file: File) => {
      formData.append('file', file, this.FileData.relativePath);

      this.solveUnmatchedGoodsService.uploadUnmatchedGoodsList(formData).subscribe(res => {
        this.messageFieldComponent.pushMessage(res);

        // エラーあり
        if (res.errorCount > 0) {
          this.toastr.error(
            'アンマッチ商品ファイルのアップロードに失敗しました', 'アップロード失敗'
          );
        }
        // 警告あり
        else if (res.warningCount > 0) {
          this.downloadUpdateFile(res.fileName);
          this.toastr.warning(
            'アンマッチ商品ファイルのアップロードで警告があります', 'アップロード警告'
          );
        }
        // 正常終了
        else {
          this.downloadUpdateFile(res.fileName);
          this.toastr.info(
            'アンマッチ商品ファイルのアップロードが完了しました', 'アップロード完了'
          );
        }
      });
    });
  }

  /**
   * 指定されたアンマッチ商品差分ファイルをダウンロードします
   * @param fileName ファイル名（ファイルパス）
   */
  private downloadUpdateFile(fileName: string) {
    const el = this.downloadLinkRef.nativeElement as HTMLAnchorElement;
    el.download = null;

    this.solveUnmatchedGoodsService.downloadGoodsList(fileName).subscribe(x => {
      el.download = 'アンマッチ商品差分ファイル.xlsx';
      el.href = URL.createObjectURL(x);
      el.click();
    });
  }

  /**
   * ServiceNowの申請ページを開く
   */
  public applyOnServicenow() {
    window.open(environment.servicenow.masterMaintenanceURL, '_blank');
  }

  /**
   * ServiceNoの申請履歴ページを開く
   */
  public openApplyList() {
    window.open(environment.servicenow.myRequestsURL, '_blank');
  }

  /**
   * オートコンプリート表示処理
   */
  public autoCompleteDisplay(value: any) {
    if (!value) {
      return '';
    }
    const chain = this.chainList.find(x => x.chainCd === value);
    return `${chain.chainCd} ${chain.chainName}`;
  }

  /**
   * オートコンプリートフィルタリング
   * @param filteringStr フィルタリングする文字列
   */
  private autoCompleteFilter(filteringStr: any) {
    let filteredKeys = '';
    filteredKeys = filteringStr.toLowerCase();
    const filtered = this.chainList.filter(it => it.chainName.toLowerCase().includes(filteredKeys) || it.chainCd.includes(filteredKeys));
    return filtered;
  }

  /** ダウンロードボタンクリック */
  public async clickedDownLoad() {
    // 入力チェック
    const chainCd = this.chainListControl.value;
    if (!chainCd) {
      this.toastr.error('ダウンロードするチェーンを選択してください。');
      return;
    }
    const chain = this.chainList.find(x => x.chainCd === this.chainListControl.value);

    this.solveUnmatchedGoodsService.checkUnmatchedGoodsListExist({
      chain: chain.chainCd,
      organization: environment.organization
    }).subscribe(x => {
      if (!x.exist) {
        this.toastr.info(x.message);
        return;
      }
      // ダウンロード開始
      this.solveUnmatchedGoodsService.downloadUnmatchedGoodsList({
        chain: chain.chainCd,
        organization: environment.organization
      }).subscribe(res => {
        const el = this.downloadLinkRef.nativeElement as HTMLAnchorElement;
        el.download = `アンマッチリスト_${chain.chainName}.xlsx`;
        el.href = URL.createObjectURL(res);
        el.click();
      });
    });
  }
}
